import { useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import type { ResultsOfflineRequestProps } from '../../shared/results_page/results_offline_request.js';
import type { ResultsFilteredEmptyCopy } from '../../shared/results_page/results_filtered_empty.js';
import {
  ResultsShareButton,
  resultsShareOf,
  type ResultsPdfShare,
  type ResultsShare,
} from '../../shared/results_page/results_share.js';
import {
  decisionNotice,
  type ResultsNoticeView,
} from '../../shared/results_page/results_notice.js';
import {
  ResultsLayout,
  type ResultsLayoutProps,
} from '../../shared/results_page/results_layout.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type { RentalMapView, RentalOfferView } from '../models.js';
import type { RentalMapSearchArea } from './rental_map.js';
import { rentalAgencyKey } from './rental_map_pins.js';
import type { RentalOfferCardCopy } from './rental_offer_card.js';
import {
  EMPTY_RENTAL_RESULTS_FILTERS,
  matchesRentalResultsFilters,
  RentalResultsFilters,
  type RentalFilterableOffer,
  type RentalFilterCatalog,
  type RentalResultsFiltersCopy,
  type RentalResultsFilterValue,
} from './rental_results_filters.js';
import {
  activeRentalFilterCount,
  RentalResultsFilterChips,
  type RentalResultsChipsCopy,
} from './rental_results_filter_chips.js';
import { RentalResultsFiltersSheet } from './rental_results_filters_sheet.js';
import { RentalResultsList } from './rental_results_list.js';
import {
  RentalResultsMapAside,
  type RentalResultsMapAsideProps,
  type RentalResultsMapCopy,
} from './rental_results_map_aside.js';
import {
  availableRentalOrder,
  RentalResultsSort,
  sortRentalOffers,
  type RentalResultsOrder,
  type RentalResultsSortCopy,
} from './rental_results_sort.js';

export type RentalResultsPageCopy = {
  /** O título de escolha, já formatado pelo host — "Carros em {lugar}". */
  title: string;
  /** A leitura que segue a contagem na linha da meta, quando o host tem uma. */
  context?: string;
  totalCarsLabel(total: number): string;
  visibleCarsLabel(visible: number, total: number): string;
  /** O nome acessível da lista ("Carros encontrados"). */
  resultsLabel: string;
  /** O compartilhar da busca, ao lado do título. Só aparece com `onShare`. */
  shareLabel: string;
  /** O botão do pé da folha de filtros do celular ("Aplicar filtros"). */
  applyFiltersLabel: string;
  /** O botão flutuante que leva ao mapa em tela cheia quando ele não cabe ao lado. */
  showMapLabel: string;
  loadMoreLabel?: string;
  /** O corpo da lista quando o corte esconde todos os carros. */
  filteredEmpty: ResultsFilteredEmptyCopy;
  sort: RentalResultsSortCopy;
  /** A fila de chips do palco estreito. Só faz falta com `chipsContainer`. */
  chips?: RentalResultsChipsCopy;
  card: RentalOfferCardCopy;
  /** Os controles de vista do mapa, o pino de agência e a folha do carro
   *  selecionado — só fazem falta com `map`. */
  map?: RentalResultsMapCopy;
};

export type RentalResultsPageProps = {
  /** O mapa ao lado da lista. Ausente ⇒ sem aside: a linha centra como a do
   *  voo — o caso da conversa, onde a busca não traz coordenada nenhuma e um
   *  painel "mapa indisponível" só ocuparia a coluna. */
  map?: RentalMapView;
  offers: readonly RentalFilterableOffer[];
  currency: string;
  copy: RentalResultsPageCopy;
  filtersCopy: RentalResultsFiltersCopy;
  /** Controlados no chamador: uma fonte só para a página e o mapa em tela cheia. */
  filters: RentalResultsFilterValue;
  order: RentalResultsOrder;
  /** O pino do mapa que filtra a lista pela agência de retirada (#3995), a
   *  `key` do pino (`rentalAgencyKey`). Controlado como os filtros (o mapa em
   *  tela cheia do host também filtra), mas FORA deles: o mapa recebe a lista
   *  com todos os filtros menos este. Um pino que saiu da cena (outro filtro o
   *  tirou) não corta nada. */
  agencyPin?: string;
  /** O toque no pino e o `Redefinir`. Sem ele o pino é só leitura. */
  onAgencyPinChange?: (key: string | undefined) => void;
  selectedOfferId?: string;
  locked?: boolean;
  /** O total do fornecedor; ignorado quando menor que a lista já carregada. */
  total?: number;
  /** As opções de categoria e locadora do servidor (`rentalFilterCatalogOf`):
   *  estáveis enquanto a rolagem traz páginas. Ausente ⇒ saem do carregado. */
  catalog?: RentalFilterCatalog;
  hasMore?: boolean;
  loadingMore?: boolean;
  notice?: ResultsNoticeView;
  onQueryChange(query: { filters: RentalResultsFilterValue; order: RentalResultsOrder }): void;
  onLoadMore?: () => void;
  /** Abre o mapa em tela cheia quando ele não cabe ao lado. Só com `map`. */
  onMapExpand?: () => void;
  /** O "buscar nesta área" do mapa (#3995), no lateral e no palco do celular.
   *  Só com `map`; ausente ⇒ o mapa não o oferece. */
  searchArea?: RentalMapSearchArea;
  /** `Selecionar` no card: direto ao checkout, sem tela intermediária. */
  onSelect(offerId: string): void;
  onShare?: () => void;
  /** O "Baixar PDF" do compartilhar (#5156): com ele o botão vira o menu, e o
   *  PDF recebe os carros na ordem e com os filtros da tela. */
  pdfShare?: ResultsPdfShare<RentalOfferView>;
  /** Ações do host ao lado do título, depois do compartilhar — na conversa, o
   *  "Ver políticas" do viajante. */
  titleActions?: ReactNode;
  /**
   * Onde a fila de chips de filtro/ordenação monta no palco ESTREITO — a
   * segunda linha da top bar do host, sob a pílula de busca, como no hotel
   * (#3140). Com o slot (e `copy.chips`), a linha da meta fica só com a
   * contagem. Ausente ⇒ `Filtros` e o ordenador seguem na linha da meta.
   */
  chipsContainer?: Element | null;
  /** A porta do pedido offline à equipe VOLL no pé da lista
   *  (`ResultsOfflineRequest`), quando a lista acabou: copy e envio são do host.
   *  Ausente ⇒ sem porta. */
  offlineRequest?: ResultsOfflineRequestProps;
  /** Quem rola a lista na linha (`ResultsLayout`): o host (o padrão) ou a
   *  própria página, quando o host lhe dá altura travada (`own`, #4587). */
  listScroll?: ResultsLayoutProps['listScroll'];
};

/** A copy da casca: os rótulos dos filtros e o do mapa ao lado. */
function layoutCopyOf(
  filtersCopy: RentalResultsFiltersCopy,
  copy: RentalResultsPageCopy,
): ResultsLayoutProps['copy'] {
  return {
    filtersLabel: filtersCopy.filtersLabel,
    resetFiltersLabel: filtersCopy.resetFiltersLabel,
    asideLabel: copy.showMapLabel,
  };
}

/** A cadeia de derivação das ofertas: o corte e a ordem primeiro, a projeção
 *  para a VIEW depois, todas memoizadas — array novo a cada render faria a
 *  lista e o mapa remontarem por quadro. A `scene` é a do mapa (todos os
 *  filtros menos o pino); `listed` e `views`, a da lista (com o pino). */
function useRentalPageOffers(
  offers: readonly RentalFilterableOffer[],
  filters: RentalResultsFilterValue,
  order: RentalResultsOrder,
  agencyPin: string | undefined,
) {
  const scene = useMemo(
    () =>
      sortRentalOffers(
        offers.filter((facts) => matchesRentalResultsFilters(facts, filters)),
        order,
      ),
    [filters, offers, order],
  );
  const pinned = scene.some(({ offer }) => rentalAgencyKey(offer) === agencyPin)
    ? agencyPin
    : undefined;
  const listed = useMemo(
    () =>
      pinned === undefined ? scene : scene.filter(({ offer }) => rentalAgencyKey(offer) === pinned),
    [pinned, scene],
  );
  const views = useMemo(() => listed.map(({ offer }) => offer), [listed]);
  return { scene, pinned, listed, views };
}

/** A linha da meta: a contagem ("3 de 52 carros") e o contexto do host, quando
 *  houver. Enquanto a lista completa carrega, a linha diz isso no lugar da
 *  contagem (o `aria-live` do layout anuncia a carga e, depois, o número). */
function metaLabel(
  copy: RentalResultsPageCopy,
  visible: number,
  total: number,
  notice: ResultsNoticeView | undefined,
): string {
  if (notice?.tone === 'loading') return notice.title;
  const count =
    visible === total ? copy.totalCarsLabel(total) : copy.visibleCarsLabel(visible, total);
  return copy.context ? `${count} · ${copy.context}` : count;
}

/** O aside do mapa e o que o acompanha (o palco estreito e o abrir em tela
 *  cheia) — as props andam juntas, e só existem com `map`. */
function mapAside(
  props: Omit<RentalResultsMapAsideProps, 'frame'>,
): Pick<ResultsLayoutProps, 'narrowAside' | 'aside' | 'onAsideOpen'> {
  return {
    narrowAside: 'stage',
    aside: (frame) => <RentalResultsMapAside frame={frame} {...props} />,
    ...(props.onMapExpand ? { onAsideOpen: props.onMapExpand } : {}),
  };
}

/** O compartilhar e as ações do host ao lado do título — nenhum dos dois, nada. */
function titleActionsOf(
  share: ResultsShare | undefined,
  hostActions: ReactNode,
): Pick<ResultsLayoutProps, 'titleActions'> {
  if (!share && !hostActions) return {};
  return {
    titleActions: (
      <>
        {share && <ResultsShareButton share={share} />}
        {hostActions}
      </>
    ),
  };
}

/** Os chips só existem no estreito, com o slot do host aberto e a copy deles. */
function chipsSlotOf(
  narrow: boolean,
  element: Element | null | undefined,
  copy: RentalResultsChipsCopy | undefined,
): { element: Element; copy: RentalResultsChipsCopy } | null {
  return narrow && element && copy ? { element, copy } : null;
}

/** A folha de cortes do celular que o layout monta quando a coluna sai da
 *  linha — o produto por dentro, o slot (aberto/fechar/foco) do layout. */
function filtersSheetRenderer(
  deps: Omit<
    Parameters<typeof RentalResultsFiltersSheet>[0],
    'open' | 'onOpenChange' | 'returnFocusTo'
  >,
): NonNullable<ResultsLayoutProps['filtersSheet']> {
  return (slot) => <RentalResultsFiltersSheet {...slot} {...deps} />;
}

/** O total do fornecedor só vale quando não contradiz a lista já carregada — um
 *  total menor que o carregado viraria "N de M" invertido. A lista é uma
 *  AMOSTRA quando o total passa do carregado OU há mais páginas mesmo sem
 *  total: as contagens dos filtros são da amostra, e as facetas do refino
 *  seguem clicáveis em zero. */
function sampleOf(
  total: number | undefined,
  loaded: number,
  hasMore: boolean | undefined,
): { known: number | undefined; countsPartial: boolean } {
  const known = total !== undefined && total >= loaded ? total : undefined;
  return { known, countsPartial: hasMore === true || (known !== undefined && known > loaded) };
}

/** O corte em pé — o pino de agência em cena conta como um — e como desfazê-lo:
 *  o `Redefinir` da coluna e o `Limpar filtros` da lista vazia (#6268) zeram os
 *  filtros e soltam o pino GUARDADO, mesmo fora de cena — senão ele voltaria a
 *  cortar quando o filtro que o tirou de cena saísse. */
function rentalCutOf(
  filters: RentalResultsFilterValue,
  agencyPinned: boolean,
  agencyPin: string | undefined,
  applyFilters: (next: RentalResultsFilterValue) => void,
  unpin: () => void,
): { count: number; reset: () => void } {
  return {
    count: activeRentalFilterCount(filters, agencyPinned),
    reset: () => {
      applyFilters(EMPTY_RENTAL_RESULTS_FILTERS);
      if (agencyPin !== undefined) unpin();
    },
  };
}

/**
 * A página de resultados da busca DETERMINÍSTICA de carro (#2441): o layout
 * compartilhado de resultados (`ResultsLayout`, #2994) preenchido com o painel
 * de cortes do carro (e a folha dele no celular), o ordenador, os cards e o mapa
 * da agência. A contagem mora na linha da meta, como no hotel — o Figma do carro
 * a punha ao lado do título, e igualar as verticais venceu.
 */
export function RentalResultsPage({
  map,
  offers,
  currency,
  copy,
  filtersCopy,
  filters,
  order: requestedOrder,
  agencyPin,
  onAgencyPinChange,
  selectedOfferId,
  locked = false,
  total,
  catalog,
  hasMore,
  loadingMore,
  notice,
  onQueryChange,
  onLoadMore,
  onMapExpand,
  searchArea,
  onSelect,
  onShare,
  pdfShare,
  titleActions,
  chipsContainer,
  offlineRequest,
  listScroll,
}: Readonly<RentalResultsPageProps>): ReactNode {
  /* O palco ESTREITO é decidido pela régua das superfícies determinísticas
     (`NARROW_STAGE_QUERY`), não pela régua de espaço da linha: a linha diz se as
     colunas cabem; a largura da tela diz se lista e mapa são duas telas. */
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  const order = availableRentalOrder(requestedOrder, offers);
  const { scene, pinned, listed, views } = useRentalPageOffers(offers, filters, order, agencyPin);
  const applyFilters = (next: RentalResultsFilterValue) => {
    onQueryChange({ filters: next, order });
  };
  const agencyPinned = pinned !== undefined;
  const unpin = () => {
    onAgencyPinChange?.(undefined);
  };
  const { known, countsPartial } = sampleOf(total, offers.length, hasMore);
  // O que a folha de filtros e a fila de chips do estreito dividem.
  const narrowProps = {
    offers,
    catalog,
    currency,
    applyLabel: copy.applyFiltersLabel,
    locked,
    countsPartial,
    agencyPinned,
    onAgencyUnpin: unpin,
  };
  const cut = rentalCutOf(filters, agencyPinned, agencyPin, applyFilters, unpin);
  const cardNotice = decisionNotice(notice);
  const chipsSlot = chipsSlotOf(narrow, chipsContainer, copy.chips);
  return (
    <>
      {chipsSlot &&
        createPortal(
          <RentalResultsFilterChips
            {...narrowProps}
            filters={filters}
            order={order}
            filtersCopy={filtersCopy}
            sortCopy={copy.sort}
            copy={chipsSlot.copy}
            onQueryChange={onQueryChange}
          />,
          chipsSlot.element,
        )}
      <ResultsLayout
        copy={layoutCopyOf(filtersCopy, copy)}
        narrow={narrow}
        listScroll={listScroll}
        metaControls={chipsSlot ? 'none' : 'inline'}
        title={copy.title}
        {...titleActionsOf(resultsShareOf(copy.shareLabel, onShare, pdfShare, views), titleActions)}
        meta={metaLabel(copy, listed.length, known ?? offers.length, notice)}
        metaActions={
          <RentalResultsSort
            value={order}
            offers={offers}
            copy={copy.sort}
            disabled={locked}
            onChange={(next) => {
              onQueryChange({ filters, order: next });
            }}
          />
        }
        activeFilterCount={cut.count}
        onResetFilters={cut.reset}
        filters={
          <RentalResultsFilters
            offers={offers}
            {...(catalog ? { catalog } : {})}
            currency={currency}
            value={filters}
            copy={filtersCopy}
            locked={locked}
            countsPartial={countsPartial}
            frame={false}
            header={false}
            onChange={applyFilters}
          />
        }
        filtersSheet={filtersSheetRenderer({
          ...narrowProps,
          value: filters,
          copy: filtersCopy,
          onApply: applyFilters,
        })}
        content={
          <RentalResultsList
            offers={views}
            resultsLabel={copy.resultsLabel}
            cardCopy={copy.card}
            {...(selectedOfferId ? { selectedOfferId } : {})}
            busy={locked}
            {...(cardNotice ? { notice: cardNotice } : {})}
            pending={notice?.tone === 'loading'}
            hasMore={hasMore === true}
            loadingMore={loadingMore === true}
            {...(copy.loadMoreLabel ? { loadMoreLabel: copy.loadMoreLabel } : {})}
            {...(onLoadMore ? { onLoadMore } : {})}
            {...(offlineRequest ? { offlineRequest } : {})}
            {...(cut.count > 0 ? { filteredEmpty: copy.filteredEmpty } : {})}
            onClearFilters={cut.reset}
            onSelect={onSelect}
          />
        }
        {...(map && copy.map
          ? mapAside({
              map,
              offers: scene,
              selectedOfferId,
              pinnedKey: pinned,
              onPinnedKeyChange: onAgencyPinChange,
              copy: copy.map,
              cardCopy: copy.card,
              locked,
              onMapExpand,
              onSelect,
              searchArea,
            })
          : {})}
      />
    </>
  );
}
